/**
 * taskRowMath.ts
 * Pure presentation math for the scenario task table: the "Machine Time" cell
 * and the even split of a test quantity across parallel machines.
 *
 * Why a shared module: both Phase 1 (machine planning) and the multi-machine
 * group rows render these, and the rules are subtle enough that two copies would
 * drift. No React, no I/O — unit-testable.
 */

import { isContinuousRun } from '../../../../utils/runModeRendering';
import { continuousSpanDays } from '../../../../utils/runMode';

/** Group US-style thousands separators ('1,125'). */
function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export interface MachineTimeText {
  /** Total machine minutes the task occupies (per-unit × qty). */
  total: number;
  /** Per-test (or per-specimen, for prep) machine minutes. */
  perUnit: number;
  /** Unit count the total scales by (TestQty, floored at 1). */
  qty: number;
  /** Inline cell text — "45 × 25 = 1,125 min" (qty>1) or "45 min" (qty≤1). */
  text: string;
  /** Hover explanation of the breakdown. */
  tooltip: string;
}

/**
 * "Show both inline" Machine Time (Gigi, 2026-06-19).
 *
 * - Test rows: `machineMinutes` IS the PER-TEST rate → derive the total by
 *   multiplying by qty and render "rate × qty = total min".
 * - Prep rows: prep is a ONE-TIME block per test line (lab rule 2026-07-16),
 *   independent of the specimen count, so `machineMinutes` IS the whole prep
 *   total → render it flat ("15 min"), never a per-specimen "× qty" equation.
 *
 * Run Mode Phase C: continuous (24/7) test rows label the total as ELAPSED time
 * and append the ~calendar-day span ('6,300 × 5 = 31,500 min elapsed (~22 cal
 * days)'). Prep is always attended, so it never takes the continuous path.
 *
 * Returns null when there's no machine-time information (renders '—').
 */
export function formatMachineTime(
  machineMinutes: number | null,
  testQty: number | null,
  isPrep: boolean,
  runMode?: string | null,
): MachineTimeText | null {
  if (machineMinutes == null || !Number.isFinite(machineMinutes)) return null;

  const qty = testQty != null && testQty > 0 ? Math.floor(testQty) : 1;

  // Prep is a ONE-TIME block per test line (lab rule 2026-07-16), independent of the
  // specimen count — show the flat total, never a per-specimen "× qty" equation.
  if (isPrep) {
    const total = machineMinutes;
    const text = `${fmt(total)} min`;
    const tooltip =
      qty > 1
        ? `${fmt(total)} min — one-time prep for this test (covers ${qty} specimens)`
        : `${fmt(total)} min — one-time prep for this test`;
    return { total, perUnit: total, qty, text, tooltip };
  }

  const perUnit = machineMinutes; // test rows store the per-test rate
  const total = perUnit * qty;

  // Continuous: minutes are total ELAPSED 24/7 run-time, so surface the ~calendar-day span.
  if (isContinuousRun(runMode)) {
    const calDays = continuousSpanDays(total);
    const text =
      qty > 1
        ? `${fmt(perUnit)} × ${qty} = ${fmt(total)} min elapsed (~${calDays} cal days)`
        : `${fmt(total)} min elapsed (~${calDays} cal day${calDays !== 1 ? 's' : ''})`;
    const tooltip =
      qty > 1
        ? `${fmt(perUnit)} min/test × ${qty} tests = ${fmt(total)} min total elapsed run-time (24/7) — ~${calDays} calendar days on one machine`
        : `${fmt(total)} min elapsed run-time (24/7) — ~${calDays} calendar day${calDays !== 1 ? 's' : ''}`;
    return { total, perUnit, qty, text, tooltip };
  }

  const text = qty > 1 ? `${fmt(perUnit)} × ${qty} = ${fmt(total)} min` : `${fmt(total)} min`;
  const tooltip =
    qty > 1
      ? `${fmt(perUnit)} min/test × ${qty} tests = ${fmt(total)} min total machine time`
      : `${fmt(total)} min machine time`;

  return { total, perUnit, qty, text, tooltip };
}

/**
 * Split a whole `total` (e.g. 25 tests) into `parts` shares as evenly as possible,
 * handing the remainder to the earliest shares so the bigger pieces come first:
 *
 *   evenSplit(25, 2) → [13, 12]
 *   evenSplit(25, 3) → [9, 8, 8]
 *   evenSplit(4, 4)  → [1, 1, 1, 1]
 *
 * Used to show each parallel machine's share of the test quantity on its own row.
 * Returns [] for non-positive `parts`; clamps a null/NaN `total` to 0.
 */
export function evenSplit(total: number | null, parts: number): number[] {
  if (!Number.isFinite(parts) || parts <= 0) return [];
  const n = total != null && Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const base = Math.floor(n / parts);
  const rem = n - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < rem ? 1 : 0));
}
